import React, { useEffect, useState } from 'react';
import { Drawer } from 'syngenta-digital-cropwise-react-ui-kit';
import CloseIcon from 'components/Icons/CloseIcon';
import LoadingSpinner from 'components/LoadingSpinner';
import { OperationType } from 'components/RecordsListView/constants';
import { OperationDetails } from 'screens/Applications&WorkOrders/Components/types';
import { OperationPropertyVO } from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import {
  FullProduct,
  TankInformation
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import DrawerHeader from './DrawerHeader';
import DrawerTabs from './DrawerTabs';
import {
  DRAWER_CONTENT_CLASS,
  DRAWER_MIN_WIDTH_PX,
  DRAWER_WIDTH_RATIO
} from './constants';
import './index.css';

const getDrawerWidth = () => {
  if (globalThis.window === undefined) return DRAWER_MIN_WIDTH_PX;
  return Math.max(
    Math.round(globalThis.window.innerWidth * DRAWER_WIDTH_RATIO),
    DRAWER_MIN_WIDTH_PX
  );
};

export interface LinkedRecordOverviewDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Record-type badge for the header, sized 16x16 by the caller. */
  icon: React.ReactNode;
  /** Uppercase type label, e.g. `WORK ORDER`. */
  typeLabel: string;
  title: string;
  /**
   * Crop seasons for the header. Taken from the row the drawer was opened from
   * where the caller has one, so the header reads correctly while the detail
   * request is still in flight and if it fails.
   */
  cropSeasonIds?: string[];
  type: OperationType;
  details?: OperationDetails;
  products: FullProduct[];
  propertiesData: OperationPropertyVO[];
  tankInformation?: TankInformation;
  isLoading: boolean;
  hasError: boolean;
  footer: React.ReactNode;
}

/**
 * Quick view of a record linked to the one being viewed, shown over the host
 * page. Every Linked Records entry point renders this same drawer; what differs
 * between them is where the data comes from and where the footer navigates,
 * both of which the caller supplies.
 */
const LinkedRecordOverviewDrawer = ({
  open,
  onClose,
  icon,
  typeLabel,
  title,
  cropSeasonIds,
  type,
  details,
  products,
  propertiesData,
  tankInformation,
  isLoading,
  hasError,
  footer
}: LinkedRecordOverviewDrawerProps) => {
  const [drawerWidth, setDrawerWidth] = useState(getDrawerWidth);

  useEffect(() => {
    const handleResize = () => setDrawerWidth(getDrawerWidth());
    globalThis.window?.addEventListener('resize', handleResize);
    return () => globalThis.window?.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Drawer
      className="linked-record-overview-drawer"
      placement="right"
      open={open}
      onClose={onClose}
      closable={false}
      maskClosable={false}
      keyboard
      width={drawerWidth}
      footer={footer}
    >
      <div
        className={`relative flex flex-col h-full min-h-0 ${DRAWER_CONTENT_CLASS}`}
      >
        {/* Padding places the 18px glyph 16 from the top and 14 from the right
            edge of the panel, as drawn, while keeping a 34px hit area. */}
        <button
          type="button"
          aria-label="Close"
          data-testid="linked-record-drawer-header-close"
          onClick={onClose}
          className="absolute p-2 focus:outline-hidden focus:shadow-outline"
          style={{ top: 8, right: 6 }}
        >
          <CloseIcon />
        </button>

        <div className="flex flex-col flex-1 min-h-0 px-8">
          {/* While loading the spinner takes the whole panel: the header
              would only show placeholders. */}
          {!isLoading && (
            <DrawerHeader
              icon={icon}
              typeLabel={typeLabel}
              title={title}
              cropSeasonIds={cropSeasonIds}
            />
          )}

          {isLoading && (
            <div
              data-testid="linked-record-drawer-loading"
              className="flex flex-1 items-center justify-center py-16"
            >
              <LoadingSpinner message="Loading overview..." />
            </div>
          )}

          {!isLoading && hasError && (
            <div
              role="alert"
              data-testid="linked-record-drawer-error"
              className="flex items-center justify-center py-8 text-neutral-50"
            >
              Unable to load record details.
            </div>
          )}

          {!isLoading && !hasError && details && (
            <DrawerTabs
              type={type}
              details={details}
              products={products}
              propertiesData={propertiesData}
              tankInformation={tankInformation}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default LinkedRecordOverviewDrawer;
